import { LinkedInWizardData } from "@/pages/LinkedInWizard";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, Plus, X, Target, Briefcase, Award, Zap } from "lucide-react";
import { useState } from "react";

interface LinkedInWizardStep10Props {
  data: LinkedInWizardData;
  updateData: (updates: Partial<LinkedInWizardData>) => void;
  onNext: () => void;
  onPrev: () => void;
}

// Suggested keywords based on common industries and roles
const SUGGESTED_KEYWORDS = {
  general: [
    "Leadership", "Strategic Planning", "Team Management", "Project Management", 
    "Problem Solving", "Communication", "Analytics", "Innovation", "Growth Strategy", "Optimization"
  ],
  tech: [
    "Software Development", "Cloud Computing", "Data Analysis", "Cybersecurity", 
    "AI/Machine Learning", "DevOps", "Agile", "API Development", "Database Management", "SaaS"
  ],
  marketing: [
    "Digital Marketing", "Content Strategy", "SEO/SEM", "Social Media", "Brand Management", 
    "Campaign Management", "Lead Generation", "Marketing Automation", "Customer Acquisition", "ROI Optimization"
  ],
  sales: [
    "Sales Strategy", "Business Development", "Customer Relations", "Revenue Growth", 
    "Pipeline Management", "Negotiation", "Account Management", "Market Expansion", "CRM", "Sales Operations"
  ],
  finance: [
    "Financial Analysis", "Budget Management", "Risk Assessment", "Investment Strategy", 
    "Cost Optimization", "Financial Planning", "Compliance", "Audit", "Forecasting", "Financial Modeling"
  ],
  healthcare: [
    "Patient Care", "Healthcare Management", "Medical Technology", "Compliance", 
    "Quality Improvement", "Clinical Research", "Healthcare Analytics", "Regulatory Affairs", "Care Coordination", "Medical Administration"
  ]
};

const JOB_TITLE_SUGGESTIONS = [
  "Manager", "Director", "Senior", "Lead", "Specialist", "Coordinator", "Analyst", 
  "Consultant", "Executive", "VP", "Head of", "Principal", "Senior Manager", "Team Lead"
];

const SKILL_CATEGORIES = [
  { 
    name: "Leadership", 
    icon: Target,
    skills: ["Team Leadership", "Strategic Planning", "Change Management", "Executive Leadership", "Vision Setting"]
  },
  { 
    name: "Technical", 
    icon: Zap,
    skills: ["Data Analysis", "Project Management", "Process Improvement", "Software Proficiency", "Technical Writing"]
  },
  { 
    name: "Communication", 
    icon: Award,
    skills: ["Public Speaking", "Presentation Skills", "Stakeholder Management", "Cross-functional Collaboration", "Negotiation"]
  },
  { 
    name: "Business", 
    icon: Briefcase,
    skills: ["Business Strategy", "Market Analysis", "Financial Management", "Operations Management", "Customer Relations"]
  }
];

export function LinkedInWizardStep10({ data, updateData }: LinkedInWizardStep10Props) {
  const [customKeyword, setCustomKeyword] = useState("");
  const [customJobTitle, setCustomJobTitle] = useState("");
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>(
    data.keywords ? data.keywords.split(', ').filter(k => k.trim()) : []
  );
  const [selectedJobTitles, setSelectedJobTitles] = useState<string[]>(
    data.targetJobTitles ? data.targetJobTitles.split(', ').filter(t => t.trim()) : []
  );
  const [selectedSkills, setSelectedSkills] = useState<string[]>(
    data.skills ? data.skills.split(', ').filter(s => s.trim()) : []
  );

  const addKeyword = (keyword: string) => {
    if (!selectedKeywords.includes(keyword) && selectedKeywords.length < 15) {
      const newKeywords = [...selectedKeywords, keyword];
      setSelectedKeywords(newKeywords);
      updateData({ keywords: newKeywords.join(', ') });
    }
  };

  const removeKeyword = (keyword: string) => {
    const newKeywords = selectedKeywords.filter(k => k !== keyword);
    setSelectedKeywords(newKeywords);
    updateData({ keywords: newKeywords.join(', ') });
  };

  const addCustomKeyword = () => {
    if (customKeyword.trim() && !selectedKeywords.includes(customKeyword.trim())) {
      addKeyword(customKeyword.trim());
      setCustomKeyword("");
    }
  };

  const addJobTitle = (title: string) => {
    if (!selectedJobTitles.includes(title) && selectedJobTitles.length < 10) {
      const newTitles = [...selectedJobTitles, title];
      setSelectedJobTitles(newTitles);
      updateData({ targetJobTitles: newTitles.join(', ') });
    }
  };

  const removeJobTitle = (title: string) => {
    const newTitles = selectedJobTitles.filter(t => t !== title);
    setSelectedJobTitles(newTitles);
    updateData({ targetJobTitles: newTitles.join(', ') });
  };

  const addCustomJobTitle = () => {
    if (customJobTitle.trim() && !selectedJobTitles.includes(customJobTitle.trim())) {
      addJobTitle(customJobTitle.trim());
      setCustomJobTitle("");
    }
  };

  const addSkill = (skill: string) => {
    if (!selectedSkills.includes(skill) && selectedSkills.length < 20) {
      const newSkills = [...selectedSkills, skill];
      setSelectedSkills(newSkills);
      updateData({ skills: newSkills.join(', ') });
    }
  };

  const removeSkill = (skill: string) => {
    const newSkills = selectedSkills.filter(s => s !== skill);
    setSelectedSkills(newSkills);
    updateData({ skills: newSkills.join(', ') });
  };

  // Get personalized keyword recommendations based on user data
  const getPersonalizedKeywords = () => {
    const industries = data.industries || [];
    const goals = data.primaryGoals || [];
    const userCategories = data.userCategories || [];
    const sellingPoints = data.uniqueSellingPoints || [];
    
    let personalizedKeywords: string[] = [];
    
    // Base keywords from industry
    const industryString = industries.join(' ').toLowerCase();
    if (industryString.includes('technology') || industryString.includes('software')) {
      personalizedKeywords.push("Digital Transformation", "Innovation Strategy", "Technology Leadership", "Software Architecture", "Cloud Solutions");
    }
    if (industryString.includes('marketing') || industryString.includes('advertising')) {
      personalizedKeywords.push("Brand Strategy", "Customer Engagement", "Digital Campaigns", "Market Intelligence", "Growth Marketing");
    }
    if (industryString.includes('sales') || industryString.includes('business development')) {
      personalizedKeywords.push("Revenue Optimization", "Strategic Partnerships", "Client Success", "Market Expansion", "Relationship Building");
    }
    if (industryString.includes('finance') || industryString.includes('banking')) {
      personalizedKeywords.push("Financial Strategy", "Risk Management", "Investment Analysis", "Capital Markets", "Financial Operations");
    }
    if (industryString.includes('healthcare') || industryString.includes('medical')) {
      personalizedKeywords.push("Patient Outcomes", "Healthcare Innovation", "Clinical Excellence", "Healthcare Technology", "Medical Leadership");
    }
    if (industryString.includes('hospitality') || industryString.includes('food')) {
      personalizedKeywords.push("Guest Experience", "Operations Excellence", "Service Leadership", "Hospitality Management", "Customer Satisfaction");
    }
    
    // Keywords based on user categories
    const categoryString = userCategories.join(' ').toLowerCase();
    if (categoryString.includes('executive') || categoryString.includes('c-suite')) {
      personalizedKeywords.push("Executive Leadership", "Strategic Vision", "Board Relations", "Organizational Growth", "Stakeholder Management");
    }
    if (categoryString.includes('manager') || categoryString.includes('director')) {
      personalizedKeywords.push("Team Leadership", "Performance Management", "Cross-functional Collaboration", "Process Optimization", "Strategic Planning");
    }
    if (categoryString.includes('entrepreneur') || categoryString.includes('founder')) {
      personalizedKeywords.push("Business Development", "Innovation", "Startup Growth", "Market Disruption", "Venture Building");
    }
    
    // Keywords based on goals
    goals.forEach(goal => {
      const goalLower = goal.toLowerCase();
      if (goalLower.includes('network') || goalLower.includes('connection')) {
        personalizedKeywords.push("Professional Networking", "Industry Connections", "Thought Leadership");
      }
      if (goalLower.includes('job') || goalLower.includes('career')) {
        personalizedKeywords.push("Career Development", "Professional Growth", "Industry Expertise");
      }
      if (goalLower.includes('brand') || goalLower.includes('thought leader')) {
        personalizedKeywords.push("Personal Branding", "Industry Insights", "Content Creation");
      }
    });
    
    // Add general high-value keywords
    personalizedKeywords.push("Results-Driven", "Strategic Thinking", "Problem Solving", "Performance Excellence", "Continuous Improvement");
    
    return [...new Set(personalizedKeywords)].slice(0, 15); // Remove duplicates and limit
  };

  // Get personalized job title recommendations
  const getPersonalizedJobTitles = () => {
    const industries = data.industries || [];
    const userCategories = data.userCategories || [];
    const goals = data.primaryGoals || [];
    
    let personalizedTitles: string[] = [];
    
    // Base titles from user categories
    const categoryString = userCategories.join(' ').toLowerCase();
    if (categoryString.includes('executive') || categoryString.includes('c-suite')) {
      personalizedTitles.push("Chief Executive Officer", "Chief Operating Officer", "Vice President", "Executive Director", "Managing Director");
    }
    if (categoryString.includes('manager') || categoryString.includes('director')) {
      personalizedTitles.push("Senior Manager", "Director", "Head of Operations", "Regional Manager", "Department Head");
    }
    if (categoryString.includes('specialist') || categoryString.includes('expert')) {
      personalizedTitles.push("Senior Specialist", "Subject Matter Expert", "Principal Consultant", "Lead Analyst", "Technical Lead");
    }
    if (categoryString.includes('entrepreneur') || categoryString.includes('founder')) {
      personalizedTitles.push("Founder", "Co-Founder", "Entrepreneur", "Business Owner", "Startup CEO");
    }
    
    // Industry-specific titles
    const industryString = industries.join(' ').toLowerCase();
    if (industryString.includes('technology')) {
      personalizedTitles.push("Technology Director", "IT Manager", "Software Development Manager", "Chief Technology Officer", "Digital Innovation Lead");
    }
    if (industryString.includes('marketing')) {
      personalizedTitles.push("Marketing Director", "Brand Manager", "Digital Marketing Manager", "Chief Marketing Officer", "Growth Marketing Lead");
    }
    if (industryString.includes('sales')) {
      personalizedTitles.push("Sales Director", "Business Development Manager", "Account Executive", "Chief Revenue Officer", "Sales Team Lead");
    }
    if (industryString.includes('finance')) {
      personalizedTitles.push("Finance Director", "Financial Manager", "Chief Financial Officer", "Investment Manager", "Financial Analyst");
    }
    if (industryString.includes('healthcare')) {
      personalizedTitles.push("Healthcare Administrator", "Medical Director", "Clinical Manager", "Healthcare Operations Manager", "Patient Care Director");
    }
    
    return [...new Set(personalizedTitles)].slice(0, 10); // Remove duplicates and limit
  };

  // Get personalized skill recommendations
  const getPersonalizedSkills = () => {
    const userCategories = data.userCategories || [];
    const goals = data.primaryGoals || [];
    const sellingPoints = data.uniqueSellingPoints || [];
    const industries = data.industries || [];
    
    let personalizedSkills: string[] = [];
    
    // Skills based on user categories
    const categoryString = userCategories.join(' ').toLowerCase();
    if (categoryString.includes('executive') || categoryString.includes('c-suite')) {
      personalizedSkills.push("Executive Leadership", "Strategic Planning", "Board Governance", "Mergers & Acquisitions", "Change Management");
    }
    if (categoryString.includes('manager')) {
      personalizedSkills.push("Team Management", "Performance Coaching", "Budget Management", "Process Improvement", "Cross-functional Leadership");
    }
    if (categoryString.includes('specialist')) {
      personalizedSkills.push("Technical Expertise", "Industry Knowledge", "Problem Solving", "Analytical Skills", "Research & Development");
    }
    
    // Skills based on selling points
    sellingPoints.forEach(point => {
      const pointLower = point.toLowerCase();
      if (pointLower.includes('lead') || pointLower.includes('manage')) {
        personalizedSkills.push("Leadership Development", "Team Building", "Conflict Resolution");
      }
      if (pointLower.includes('analyz') || pointLower.includes('data')) {
        personalizedSkills.push("Data Analysis", "Business Intelligence", "Reporting & Analytics");
      }
      if (pointLower.includes('innovat') || pointLower.includes('creat')) {
        personalizedSkills.push("Innovation Management", "Creative Problem Solving", "Design Thinking");
      }
    });
    
    // Industry-specific skills
    const industryString = industries.join(' ').toLowerCase();
    if (industryString.includes('technology')) {
      personalizedSkills.push("Digital Strategy", "Technology Implementation", "Agile Methodology", "Software Development", "Cybersecurity");
    }
    if (industryString.includes('marketing')) {
      personalizedSkills.push("Digital Marketing", "Content Strategy", "SEO/SEM", "Brand Management", "Customer Acquisition");
    }
    if (industryString.includes('finance')) {
      personalizedSkills.push("Financial Analysis", "Risk Assessment", "Investment Strategy", "Financial Planning", "Regulatory Compliance");
    }
    
    // Add universal high-value skills
    personalizedSkills.push("Communication", "Negotiation", "Project Management", "Strategic Thinking", "Customer Relations");
    
    return [...new Set(personalizedSkills)].slice(0, 20); // Remove duplicates and limit
  };

  // Get relevant keyword suggestions based on user's industry (fallback function)
  const getRelevantKeywords = () => {
    const industries = data.industries || [];
    const industryString = industries.join(' ').toLowerCase();
    
    let relevantKeywords = [...SUGGESTED_KEYWORDS.general];
    
    if (industryString.includes('technology') || industryString.includes('software')) {
      relevantKeywords = [...relevantKeywords, ...SUGGESTED_KEYWORDS.tech];
    }
    if (industryString.includes('marketing') || industryString.includes('advertising')) {
      relevantKeywords = [...relevantKeywords, ...SUGGESTED_KEYWORDS.marketing];
    }
    if (industryString.includes('sales') || industryString.includes('business development')) {
      relevantKeywords = [...relevantKeywords, ...SUGGESTED_KEYWORDS.sales];
    }
    if (industryString.includes('finance') || industryString.includes('banking')) {
      relevantKeywords = [...relevantKeywords, ...SUGGESTED_KEYWORDS.finance];
    }
    if (industryString.includes('healthcare') || industryString.includes('medical')) {
      relevantKeywords = [...relevantKeywords, ...SUGGESTED_KEYWORDS.healthcare];
    }
    
    return [...new Set(relevantKeywords)]; // Remove duplicates
  };
  return (
    <div className="space-y-8">
      {/* Step Number and Header */}
      <div className="relative">
        <div className="absolute -top-2 -left-2">
          <div className="w-12 h-12 bg-linkedin rounded-full flex items-center justify-center shadow-linkedin">
            <span className="text-xl font-bold text-white">10</span>
          </div>
        </div>
        <div className="ml-12 text-center">
          <h2 className="text-2xl font-bold mb-2 text-linkedin">🎯 Help People Find You!</h2>
          <p className="text-linkedin/70 mb-6">
            Let's make sure you show up when the right people search for someone like you on LinkedIn.
          </p>
        </div>
      </div>

      {/* Keywords Section */}
      <Card className="p-6 bg-gradient-to-br from-linkedin-light to-blue-50 dark:from-linkedin-dark/20 dark:to-blue-950 border-linkedin/20">
        <div className="flex items-center gap-3 mb-4">
          <Search className="w-6 h-6 text-linkedin" />
          <div>
            <h3 className="text-lg font-semibold text-linkedin">
              Keywords That Matter
            </h3>
            <p className="text-sm text-linkedin/70">
              Choose words that describe what you do best ({selectedKeywords.length}/15 selected)
            </p>
          </div>
        </div>

        {/* Selected Keywords */}
        {selectedKeywords.length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-2">
              {selectedKeywords.map((keyword) => (
                <Badge
                  key={keyword}
                  className="bg-linkedin text-white px-3 py-1 flex items-center gap-2"
                >
                  {keyword}
                  <X
                    className="w-3 h-3 cursor-pointer hover:text-red-200"
                    onClick={() => removeKeyword(keyword)}
                  />
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Keyword Suggestions */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-linkedin/80">
            ✨ Recommended for you:
          </h4>
          <div className="flex flex-wrap gap-2">
            {getPersonalizedKeywords()
              .filter(keyword => !selectedKeywords.includes(keyword))
              .slice(0, 15)
              .map((keyword) => (
                <Button
                  key={keyword}
                  variant="outline"
                  size="sm"
                  onClick={() => addKeyword(keyword)}
                  className="text-xs border-linkedin/30 text-linkedin hover:bg-linkedin-light"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  {keyword}
                </Button>
              ))}
          </div>

          {/* Fallback suggestions if no personalized ones */}
          {getPersonalizedKeywords().length === 0 && (
            <>
              <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300">
                Quick picks for you:
              </h4>
              <div className="flex flex-wrap gap-2">
                {getRelevantKeywords()
                  .filter(keyword => !selectedKeywords.includes(keyword))
                  .slice(0, 12)
                  .map((keyword) => (
                    <Button
                      key={keyword}
                      variant="outline"
                      size="sm"
                      onClick={() => addKeyword(keyword)}
                      className="text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      {keyword}
                    </Button>
                  ))}
              </div>
            </>
          )}

          {/* Custom Keyword Input */}
          {selectedKeywords.length < 15 && (
            <div className="flex gap-2">
              <Input
                placeholder="Add your own keyword..."
                value={customKeyword}
                onChange={(e) => setCustomKeyword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addCustomKeyword()}
                className="flex-1"
              />
              <Button onClick={addCustomKeyword} size="sm">
                Add
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Job Titles Section */}
      <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 border-green-200 dark:border-green-800">
        <div className="flex items-center gap-3 mb-4">
          <Briefcase className="w-6 h-6 text-green-600" />
          <div>
            <h3 className="text-lg font-semibold text-green-800 dark:text-green-200">
              Dream Job Titles
            </h3>
            <p className="text-sm text-green-600 dark:text-green-400">
              What roles do you want to be considered for? ({selectedJobTitles.length}/10 selected)
            </p>
          </div>
        </div>

        {/* Selected Job Titles */}
        {selectedJobTitles.length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-2">
              {selectedJobTitles.map((title) => (
                <Badge
                  key={title}
                  className="bg-green-600 text-white px-3 py-1 flex items-center gap-2"
                >
                  {title}
                  <X
                    className="w-3 h-3 cursor-pointer hover:text-red-200"
                    onClick={() => removeJobTitle(title)}
                  />
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Job Title Suggestions */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-green-700 dark:text-green-300">
            ✨ Recommended for you:
          </h4>
          <div className="flex flex-wrap gap-2">
            {getPersonalizedJobTitles()
              .filter(title => !selectedJobTitles.includes(title))
              .slice(0, 10)
              .map((title) => (
                <Button
                  key={title}
                  variant="outline"
                  size="sm"
                  onClick={() => addJobTitle(title)}
                  className="text-xs border-green-300 text-green-700 hover:bg-green-100"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  {title}
                </Button>
              ))}
          </div>

          {/* Fallback suggestions if no personalized ones */}
          {getPersonalizedJobTitles().length === 0 && (
            <>
              <h4 className="text-sm font-medium text-green-700 dark:text-green-300">
                Popular options:
              </h4>
              <div className="flex flex-wrap gap-2">
                {JOB_TITLE_SUGGESTIONS
                  .filter(title => !selectedJobTitles.includes(title))
                  .slice(0, 8)
                  .map((title) => (
                    <Button
                      key={title}
                      variant="outline"
                      size="sm"
                      onClick={() => addJobTitle(title)}
                      className="text-xs border-green-300 text-green-700 hover:bg-green-100"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      {title}
                    </Button>
                  ))}
              </div>
            </>
          )}

          {/* Custom Job Title Input */}
          {selectedJobTitles.length < 10 && (
            <div className="flex gap-2">
              <Input
                placeholder="Add a specific job title..."
                value={customJobTitle}
                onChange={(e) => setCustomJobTitle(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addCustomJobTitle()}
                className="flex-1"
              />
              <Button onClick={addCustomJobTitle} size="sm">
                Add
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Skills Section */}
      <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950 dark:to-pink-950 border-purple-200 dark:border-purple-800">
        <div className="flex items-center gap-3 mb-6">
          <Award className="w-6 h-6 text-purple-600" />
          <div>
            <h3 className="text-lg font-semibold text-purple-800 dark:text-purple-200">
              Your Superpowers
            </h3>
            <p className="text-sm text-purple-600 dark:text-purple-400">
              What are you really good at? ({selectedSkills.length}/20 selected)
            </p>
          </div>
        </div>

        {/* Selected Skills */}
        {selectedSkills.length > 0 && (
          <div className="mb-6">
            <div className="flex flex-wrap gap-2">
              {selectedSkills.map((skill) => (
                <Badge
                  key={skill}
                  className="bg-purple-600 text-white px-3 py-1 flex items-center gap-2"
                >
                  {skill}
                  <X
                    className="w-3 h-3 cursor-pointer hover:text-red-200"
                    onClick={() => removeSkill(skill)}
                  />
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Personalized Skills Section */}
        <div className="space-y-4 mb-6">
          <h4 className="text-sm font-medium text-purple-700 dark:text-purple-300 flex items-center gap-2">
            <Zap className="w-4 h-4" />
            ✨ Recommended Skills for You:
          </h4>
          <div className="flex flex-wrap gap-2">
            {getPersonalizedSkills()
              .filter(skill => !selectedSkills.includes(skill))
              .slice(0, 15)
              .map((skill) => (
                <Button
                  key={skill}
                  variant="outline"
                  size="sm"
                  onClick={() => addSkill(skill)}
                  className="text-xs border-purple-300 text-purple-700 hover:bg-purple-100"
                  disabled={selectedSkills.length >= 20}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  {skill}
                </Button>
              ))}
          </div>
        </div>

        {/* Skill Categories */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SKILL_CATEGORIES.map((category) => {
            const Icon = category.icon;
            return (
              <div key={category.name} className="space-y-3">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-purple-600" />
                  <h4 className="text-sm font-medium text-purple-700 dark:text-purple-300">
                    {category.name}
                  </h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {category.skills
                    .filter(skill => !selectedSkills.includes(skill))
                    .map((skill) => (
                      <Button
                        key={skill}
                        variant="outline"
                        size="sm"
                        onClick={() => addSkill(skill)}
                        className="text-xs border-purple-300 text-purple-700 hover:bg-purple-100"
                        disabled={selectedSkills.length >= 20}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        {skill}
                      </Button>
                    ))}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Past Job Titles - Optional */}
      <Card className="p-6 bg-muted/20 border-muted">
        <div className="space-y-4">
          <div>
            <Label htmlFor="past-job-titles" className="text-base font-medium flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              Previous Titles (Optional)
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Add past job titles that add credibility - people often search for these too!
            </p>
          </div>
          <Textarea
            id="past-job-titles"
            placeholder="e.g., Marketing Manager, Sales Associate, Project Coordinator..."
            value={data.pastJobTitles || ""}
            onChange={(e) => updateData({ pastJobTitles: e.target.value })}
            className="min-h-[80px]"
          />
        </div>
      </Card>

      {/* Summary Card */}
      {(selectedKeywords.length > 0 || selectedJobTitles.length > 0 || selectedSkills.length > 0) && (
        <Card className="p-6 bg-gradient-to-r from-linkedin/10 to-linkedin/5 border-linkedin/30">
          <div className="text-center space-y-3">
            <h3 className="text-lg font-semibold text-linkedin">
              🎉 Looking Great!
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              {selectedKeywords.length > 0 && (
                <div>
                  <span className="font-medium text-linkedin">Keywords:</span>
                  <span className="text-muted-foreground ml-1">{selectedKeywords.length} selected</span>
                </div>
              )}
              {selectedJobTitles.length > 0 && (
                <div>
                  <span className="font-medium text-linkedin">Job Titles:</span>
                  <span className="text-muted-foreground ml-1">{selectedJobTitles.length} selected</span>
                </div>
              )}
              {selectedSkills.length > 0 && (
                <div>
                  <span className="font-medium text-linkedin">Skills:</span>
                  <span className="text-muted-foreground ml-1">{selectedSkills.length} selected</span>
                </div>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              We'll weave these naturally into your profile to help you get discovered!
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}